import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem("zrope_pwa_dismissed")) return;

    // beforeinstallprompt may fire before React mounts — index.js stashes it on window.
    if (window.__zropeInstallPrompt) { setDeferred(window.__zropeInstallPrompt); setShow(true); }
    if (isIOS()) setShow(true);

    const onInstallable = () => { setDeferred(window.__zropeInstallPrompt); setShow(true); };
    const onInstalled = () => { setShow(false); window.__zropeInstallPrompt = null; };
    window.addEventListener("zrope-installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("zrope-installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIOS()) { setIosHint(true); return; }
    const p = deferred || window.__zropeInstallPrompt;
    if (!p) { setIosHint(true); return; }
    p.prompt();
    try { await p.userChoice; } catch (_) {}
    window.__zropeInstallPrompt = null;
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => { sessionStorage.setItem("zrope_pwa_dismissed", "1"); setShow(false); };

  if (!show) return null;

  return (
    <div data-testid="pwa-install-banner"
      className="fixed bottom-24 left-4 z-40 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-white/10 bg-[#0B132B] text-white shadow-2xl lg:bottom-6">
      <div className="flex items-start gap-3 p-3.5">
        <img src={`${process.env.PUBLIC_URL || ""}/icon-192.png`} alt="ZRo Pe" className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold">Install ZRo Pe App</p>
          <p className="mt-0.5 text-xs text-white/70">
            {iosHint
              ? "Tap the Share icon, then “Add to Home Screen”."
              : "Add ZRo Pe to your home screen for one-tap access."}
          </p>
          {!iosHint && (
            <div className="mt-2.5 flex gap-2">
              <button data-testid="pwa-install-btn" onClick={install}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#14B8A6] px-3 py-1.5 text-xs font-bold text-white transition active:scale-95 hover:bg-[#0D9488]">
                <Download size={14} /> Install
              </button>
              <button data-testid="pwa-install-dismiss" onClick={dismiss}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white">
                Not now
              </button>
            </div>
          )}
          {iosHint && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#14B8A6]">
              <Share size={14} /> Share → Add to Home Screen
            </div>
          )}
        </div>
        <button data-testid="pwa-install-close" onClick={dismiss} className="shrink-0 text-white/50 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
